import _ from 'lodash';
import { createSelector } from 'reselect';
import timm from 'timm';

import CompaniesAx   from 'app/actions/company-admin/companies';
import ToastAx       from 'app/actions/toast';
import CadminApi     from 'app/apis/company-admin';
import reducerUtils  from 'app/reducers/utils';
import CadminSlx     from 'app/selectors/company-admin/';
import EntitiesSlx   from 'app/selectors/entities';



/*
 *  Actions
 */

const Types = {
  SEARCH:          'CADMIN_VOL_EVENTS_SEARCH',
  GET:             'CADMIN_VOL_EVENTS_GET',
  MARK_ENDED:      'CADMIN_VOL_EVENTS_MARK_ENDED',
  CREATE:          'CADMIN_VOL_EVENTS_CREATE',
  // CREATE_HIDDEN:  'CADMIN_VOL_EVENTS_CREATE_HIDDEN',
  UPDATE:          'CADMIN_VOL_EVENTS_UPDATE',
  ANNOUNCE_SLACK:  'CADMIN_VOL_EVENTS_ANNOUNCE_SLACK',
  REMIND_SLACK:    'CADMIN_VOL_EVENTS_REMIND_SLACK',
  PUBLISH:         'CADMIN_VOL_EVENTS_PUBLISH',
  APPROVE:         'CADMIN_VOL_EVENTS_APPROVE',
  REJECT:          'CADMIN_VOL_EVENTS_REJECT',
  ARCHIVE:         'CADMIN_VOL_EVENTS_ARCHIVE',
  UNARCHIVE:       'CADMIN_VOL_EVENTS_UNARCHIVE',
  DELETE:          'CADMIN_VOL_EVENTS_DELETE',
  UPDATE_TRACKING: 'CADMIN_VOL_EVENTS_UPDATE_TRACKING',
};

const Ax = {

  search: (companyId, params={}) => {
    const promise = CadminApi.volEventsSearch(companyId, params);
    return { type: Types.SEARCH, promise, _entities: ['volEvents', 'nonprofits', 'groups'] };
  },

  get: (companyId, volEventId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsGet(companyId, volEventId);
    return dispatch({type: Types.GET, promise, _entities: ['volEvent', 'volEventShifts', 'groups', 'employee', 'nonprofit', 'campaign', 'changeLogs']});
  },

  markEnded: (companyId, volEventId) => (dispatch, getState) => {
    const promise = CadminApi.volEventsMarkEnded(companyId, volEventId);
    promise.then(() => {
      dispatch(ToastAx.success('Event has been ended.'));
    });
    return dispatch({type: Types.MARK_ENDED, promise, volEventId, _entities: ['volEvent', 'changeLogs']});
  },

  create: (companyId, attrs, {showToast=false}={}) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsCreate(companyId, attrs);
    promise.then(() => {
      if (showToast) {
        dispatch(ToastAx.success('Event created.'));
      }
    });
    return dispatch({type: Types.CREATE, promise, _entities: ['volEvent', 'volEventShifts', 'changeLogs']});
  },

  update: (companyId, volEventId, attrs, {showToast=false}={}) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsUpdate(companyId, volEventId, attrs);
    promise.then(() => {
      if (showToast) {
        dispatch(ToastAx.success('Event updated.'));
      }
    });
    return dispatch({type: Types.UPDATE, promise, _entities: ['volEvent', 'changeLogs']});
  },

  announceInSlack: (companyId, volEventId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsAnnounceInSlack(companyId, volEventId);
    promise.then(() => {
      dispatch(ToastAx.success('Announced.'));
    });
    return dispatch({ type: Types.ANNOUNCE_SLACK, promise, volEventId });
  },

  remindInSlack: (companyId, volEventId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsRemindInSlack(companyId, volEventId);
    promise.then(() => {
      dispatch(ToastAx.success('Reminder sent.'));
    });
    return dispatch({ type: Types.REMIND_SLACK, promise, volEventId });
  },

  submit: (companyId, volEventId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsSubmit(companyId, volEventId);
    promise.then(() => {
      dispatch(ToastAx.success('Event has been submitted for review.'));
      dispatch(CompaniesAx.getAttentionNumbers(companyId, {refresh: true}));
    });
    return dispatch({type: Types.SUBMIT, promise, volEventId, _entities: ['volEvent', 'changeLogs']});
  },

  publish: (companyId, volEventId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsPublish(companyId, volEventId);
    promise.then(() => {
      dispatch(ToastAx.success('Event has been published.'));
      dispatch(CompaniesAx.getAttentionNumbers(companyId, {refresh: true}));
    });
    return dispatch({type: Types.PUBLISH, promise, volEventId, _entities: ['volEvent', 'changeLogs']});
  },

  approve: (companyId, volEventId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsApprove(companyId, volEventId);
    promise.then(() => {
      dispatch(ToastAx.success('Event has been approved and published.'));
      dispatch(CompaniesAx.getAttentionNumbers(companyId, {refresh: true}));
    });
    return dispatch({type: Types.APPROVE, promise, volEventId, _entities: ['volEvent', 'changeLogs']});
  },

  reject: (companyId, volEventId, note) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsReject(companyId, volEventId, note);
    promise.then(() => {
      dispatch(ToastAx.success('Event has been moved back to draft with changes requested.'));
      dispatch(CompaniesAx.getAttentionNumbers(companyId, {refresh: true}));
    });
    return dispatch({type: Types.REJECT, promise, volEventId, _entities: ['volEvent', 'changeLogs']});
  },

  archive: (companyId, volEventId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsArchive(companyId, volEventId);
    promise.then(() => {
      dispatch(ToastAx.success('Event has been archived.'));
    });
    return dispatch({type: Types.ARCHIVE, promise, volEventId, _entities: ['volEvent', 'changeLogs']});
  },

  unarchive: (companyId, volEventId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsPublish(companyId, volEventId);
    promise.then(() => {
      dispatch(ToastAx.success('Event has been restored back to active.'));
    });
    return dispatch({type: Types.UNARCHIVE, promise, volEventId, _entities: ['volEvent', 'changeLogs']});
  },

  delete: (companyId, volEventId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsDelete(companyId, volEventId);
    promise.then(() => {
      dispatch(ToastAx.success('Event has been deleted.'));
      dispatch(CompaniesAx.getAttentionNumbers(companyId, {refresh: true}));
    });
    return dispatch({type: Types.DELETE, promise, volEventId, _entities: ['volEvent', 'changeLogs']});
  },

  updateTracking: (companyId, volEventId, {primaryGroupId, primaryCampaignId}) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.volEventsUpdateTracking(companyId, volEventId, {primaryGroupId, primaryCampaignId});
    promise.then(() => {
      dispatch(ToastAx.success('Tracking settings have been updated.'));
    });
    return dispatch({type: Types.UPDATE_TRACKING, promise, volEventId, _entities: ['volEvent']});
  },

};

/*
 *  Reducer
 */

const initialState = {
  createPending: false,
  updatePending: false,
  trackingUpdatePending: false,
};

const reducer = reducerUtils.createReducer(initialState, {

  [`${Types.CREATE}_PENDING`]: (state, action) => {
    return {...state,
      createPending: true,
    };
  },
  [`${Types.CREATE}_RESOLVED`]: (state, action) => {
    return {...state,
      createPending: false,
    };
  },
  [`${Types.CREATE}_REJECTED`]: (state, action) => {
    return {...state,
      createPending: false,
    };
  },

  [`${Types.UPDATE}_PENDING`]: (state, action) => {
    return {...state,
      updatePending: true,
    };
  },
  [`${Types.UPDATE}_RESOLVED`]: (state, action) => {
    return {...state,
      updatePending: false,
    };
  },
  [`${Types.UPDATE}_REJECTED`]: (state, action) => {
    return {...state,
      updatePending: false,
    };
  },

  [`${Types.UPDATE_TRACKING}_PENDING`]: (state, action) => {
    return {...state,
      trackingUpdatePending: true,
    };
  },
  [`${Types.UPDATE_TRACKING}_RESOLVED`]: (state, action) => {
    return {...state,
      trackingUpdatePending: false,
    };
  },
  [`${Types.UPDATE_TRACKING}_REJECTED`]: (state, action) => {
    return {...state,
      trackingUpdatePending: false,
    };
  },

});



/*
 *  Selectors
 */

const Slx = (() => {

  const selCreatePending = state => state.companyAdmin.volEvents.createPending;
  const selUpdatePending = state => state.companyAdmin.volEvents.updatePending;
  const selTrackingUpdatePending = state => state.companyAdmin.volEvents.trackingUpdatePending;

  return {
    createPending: selCreatePending,
    updatePending: selUpdatePending,
    trackingUpdatePending: selTrackingUpdatePending,
  };

})();



export {Types, Ax, reducer, Slx};
export default {Types, Ax, reducer, Slx};
